<script setup lang="ts">
  import { useNewsArticleHeadlines } from '../../composables/data-fetching/useNewsArticles'
  import { useAnalytics } from '../../composables/analytics/useAnalytics'
  import { useScrollDirection } from '../../composables/useScrollDirection'
  import { useSearchParams } from '../../composables/data-fetching/useSearchParams'
  import type {
    ShortcutGridMSNDesignProps,
    ShortcutGridGoogleDesignProps,
    ShortcutItem,
  } from '../ShortcutGrid/ShortcutGrid.vue'
  import { useLayoutSkeletonStore } from '../../store/LayoutSkeletonStore'

  import {
    useSearchSuggestions,
    type Suggestion,
  } from '../../composables/data-fetching/useSearchSuggestions/useSearchSuggestions'
  import { useConditionalNewsVideos } from '../../composables/data-fetching/useConditionalNewsVideos'
  import LargeCardWithGridSection from '../Sections/LargeCardWithGridSection.vue'
  import HalfPageAdWithGridSection from '../Sections/HalfPageAdWithGridSection.vue'
  import { useWindowSize } from '@vueuse/core'
  import { BREAKPOINTS } from '../../theme/common'
  import { useRedirectActions } from '../../composables/useRedirectActions'
  import { hasTenantHostApi } from '../../utils/host-browser-apis/hasTenantHostApi'
  import { watch, ref, computed } from 'vue'
  import { userEngagementStore } from '../../store/userData/UserEngagementStore'
  import { storeToRefs } from 'pinia'

  const layoutSkeletonStore = useLayoutSkeletonStore()
  const { data: layoutSkeleton, isLoading } = storeToRefs(layoutSkeletonStore)
  await waitUntilNotLoading(isLoading)

  const { navigateToSearchPage } = useRedirectActions()

  interface MarketPlaceShortcutsProps {
    marketplaceShortcuts?: ShortcutItem[]
    marketplaceShortcutsProvider?: string
    isShortcutsLoading?: boolean
  }

  const { t } = useI18n()
  const { selectedCategory } = useSearchParams()
  const { width } = useWindowSize()
  const searchInput = ref<string>('')

  const { query, suggestions, openSearchSuggestionLink, openSearchLink } =
    useSearchSuggestions({
      suggestionType: 'simple',
      debounce: 90,
    })

  function updateSearch() {
    query.value = searchInput.value
  }

  const directAdCampaigns = await useDirectAds()
  const { headlines, fetchNextPage, hasMore } =
    useNewsArticleHeadlines(selectedCategory)

  const { scrollDirection } = useScrollDirection()
  const { track } = useAnalytics()
  const isFooterVisible = ref(false)
  const watchScrollDirection = ref(true)
  const userEngagement = userEngagementStore()

  const contentDisplaySettingsStore = useContentDisplaySettingsStore()
  const { featureFlags } = storeToRefs(contentDisplaySettingsStore)

  const isLocalSearchEnabled = computed(() => {
    return featureFlags.value.enableLocalNewsSearch && !hasTenantHostApi()
  })

  const searchPlaceholder = computed(() => {
    return isLocalSearchEnabled.value
      ? t('Find more news')
      : t('Search the web')
  })

  const {
    videoHeadlines,
    fetchNextPage: fetchNextVideoPage,
    hasMore: hasMoreVideos,
  } = useConditionalNewsVideos(selectedCategory, layoutSkeleton)

  const filteredHeadlines = ref<typeof headlines.value>([])
  const updateFilteredHeadlines = () => {
    if (CurrentSection.value && articlesUsedInSection.value > 0) {
      filteredHeadlines.value =
        headlines.value?.slice(articlesUsedInSection.value) || []
    } else {
      filteredHeadlines.value = headlines.value || []
      return headlines.value?.slice(articlesUsedInSection.value) || []
    }
    return headlines.value || []
  }

  const articlesUsedInSection = ref(0)
  const handleUsedArticles = (count: number) => {
    articlesUsedInSection.value = count
    updateFilteredHeadlines()
  }

  // First, add a type for the section components
  type SectionComponents = {
    LargeCardWithGridSection: typeof LargeCardWithGridSection
    HalfPageAdWithGridSection: typeof HalfPageAdWithGridSection
  }

  // Define the section components with its type
  const sectionComponents: SectionComponents = {
    LargeCardWithGridSection,
    HalfPageAdWithGridSection,
  }

  const CurrentSection = computed(() => {
    const componentName = featureFlags.value.aboveFoldSectionShown

    function isValidComponent(name: unknown): name is keyof SectionComponents {
      return typeof name === 'string' && name in sectionComponents
    }

    if (!componentName || !isValidComponent(componentName)) {
      return null
    }

    return sectionComponents[componentName]
  })

  const pageDesign = computed(
    () => featureFlags.value.homePage?.design ?? 'google'
  )

  type ShortCutGridDesign =
    | ShortcutGridMSNDesignProps
    | ShortcutGridGoogleDesignProps

  // order of design and variant of ShortcutGrid: dynamicSponsoredTiles -> sponsoredTiles -> pageDesign
  const sponsoredTilesDesign = computed(() => {
    return (
      featureFlags?.value.dynamicSponsoredTiles?.design ||
      featureFlags?.value.sponsoredTiles?.design ||
      pageDesign.value
    )
  })
  const sponsoredTilesDesignObject: ComputedRef<ShortCutGridDesign> = computed(
    () => {
      return sponsoredTilesDesign.value === 'google'
        ? { design: 'google', variant: 'default' }
        : {
            design: 'msn',
            variant:
              featureFlags?.value.dynamicSponsoredTiles?.variant ||
              featureFlags?.value.sponsoredTiles?.variant ||
              'default',
          }
    }
  )

  withDefaults(defineProps<MarketPlaceShortcutsProps>(), {
    marketplaceShortcuts: () => [],
    marketplaceShortcutsProvider: '',
  })

  function handleFetchMoreItems(type: 'news' | 'video') {
    // It should only call the tracking event when the user scrolls.
    // The check below fixes a bug where the event was being triggered on pages load.
    if (scrollDirection.value !== 'none') {
      track('NtpMoreNews_Loaded')
      userEngagement.setUserEngagement('isEngaged', true)
    }

    if (type === 'news') {
      if (hasMore.value) return fetchNextPage()
    } else if (type === 'video') {
      if (hasMoreVideos.value) return fetchNextVideoPage()
    }
  }

  function handleSearch(
    queryStr: string,
    event: 'keyboard-return' | 'search-button'
  ) {
    if (!queryStr) return

    if (isLocalSearchEnabled.value) {
      navigateToSearchPage(queryStr)
    } else {
      openSearchLink(queryStr, event)
    }
  }

  const config = useRuntimeConfig()
  const { settings } = useGlobalLayoutSettings()
  const siteDomain = settings.siteName.toLowerCase()
  usePageViewTracker()

  useSeoMeta({
    title: !checkHostBrowser() ? settings.siteName : 'New Tab',
    ogTitle: settings.siteName,
    ogDescription: `Stay updated with the latest news from ${settings.siteName}.`,
    description: `Stay updated with the latest news from ${settings.siteName}.`,
    ogImage: settings.headerLogo,
    ogUrl: `https://${siteDomain}.com`,
    ogType: 'website',
  })

  // Use a separate watcher for headlines changes
  watch(
    () => headlines.value,
    () => {
      updateFilteredHeadlines()
    },
    { deep: true }
  )

  const adPlacementActive = ref(false)
  const isTestSectionLoading = ref(true)

  const shouldShowTestSection = computed(() =>
    Boolean(CurrentSection.value && width.value >= BREAKPOINTS.LG)
  )
  const handleTestSectionLoading = (isLoading: boolean) => {
    isTestSectionLoading.value = isLoading
  }

  const leaderboardAdClass = computed(
    () => featureFlags?.value.aboveFoldSectionLeaderboardAd || ''
  )

  function handleLinkClicked(skipTo: 'footer' | 'content') {
    if (skipTo === 'footer') {
      watchScrollDirection.value = false
      isFooterVisible.value = true
    }

    track('NtpSkipLink_Clicked', { skipTo })
  }

  watch(scrollDirection, (newDirection) => {
    if (!watchScrollDirection.value) {
      watchScrollDirection.value = true
      return
    }

    isFooterVisible.value = newDirection === 'up'
  })

  watch(
    shouldShowTestSection,
    (shouldShow) => {
      if (shouldShow && !adPlacementActive.value) {
        adPlacementActive.value = true
      } else if (!shouldShow && adPlacementActive.value) {
        if (window.ayManagerEnv) {
          try {
            window.ayManagerEnv.destroy([leaderboardAdClass.value], {
              mode: 'page',
              useInstanceCodes: false,
            })
            adPlacementActive.value = false
          } catch (error) {
            console.error('[AY Debug] Error removing ad placement:', error)
          }
        }
      }
    },
    { immediate: true }
  )

  const isMenuOpen = ref(false)
  function closeMenu() {
    isMenuOpen.value = false
  }
  await Promise.all([contentDisplaySettingsStore.initialized])
</script>

<template>
  <div
    class="h-full w-full p-0"
    :class="{
      'bg-background-default dark:bg-background-neutral-subtle':
        pageDesign === 'google',
      'bg-background-neutral-surface': pageDesign === 'msn',
    }"
  >
    <Navbar
      v-model:menu-open="isMenuOpen"
      :logo-image="settings?.headerLogo"
      :tagline="settings?.tagline"
      :logo-image-dark-mode="settings?.headerLogoDarkMode"
      class="bg-inherit"
    />
    <Menu
      v-model:menu-open="isMenuOpen"
      v-model:selected-category="selectedCategory"
      @tab-category-click="closeMenu"
    />
    <LazyDoodle
      v-if="
        !featureFlags?.homePage?.doodle ||
        featureFlags.homePage.doodle === 'on' ||
        featureFlags.homePage.doodle === 'random'
      "
      :random="
        featureFlags?.homePage?.doodle &&
        featureFlags.homePage.doodle === 'random'
      "
      class="mx-auto px-2 mb-[32px] h-[200px] w-[500px]"
    />
    <LazySearchbox
      v-if="!config?.public.removeSearchBox"
      v-model="searchInput"
      class="px-6 lg:px-2"
      :variant="featureFlags?.homePage?.searchboxDesign"
      :placeholder="searchPlaceholder"
      display="solid"
      :autofocus="true"
      :suggestions="isLocalSearchEnabled ? [] : suggestions"
      @click-suggestion-item="
        (item: Suggestion) => openSearchSuggestionLink(item)
      "
      @press-enter-on-search-input="
        (q: string) => handleSearch(q, 'keyboard-return')
      "
      @press-search-button="(q: string) => handleSearch(q, 'search-button')"
      @update-suggestions="updateSearch"
    />
    <div class="group focus-within:flex focus-within:gap-2 w-full">
      <a
        href="#news-content"
        class="sr-only group-focus-within:not-sr-only focus:underline flex-1 text-center p-4 bg-gray-600 dark:bg-gray-300 text-white dark:text-black border-transparent focus:border-black transition duration-200"
        @click="handleLinkClicked('content')"
      >
        Skip to Content
      </a>
      <a
        href="#footer-content"
        class="sr-only group-focus-within:not-sr-only focus:underline flex-1 text-center p-4 bg-gray-600 dark:bg-gray-300 text-white dark:text-black border-transparent focus:border-black transition duration-200"
        @click="handleLinkClicked('footer')"
      >
        Skip to Footer
      </a>
    </div>

    <LazyShortcutGrid
      class="mb-[88px] mt-[16px]"
      :shortcuts="marketplaceShortcuts"
      :provider="marketplaceShortcutsProvider"
      size="1920"
      v-bind="sponsoredTilesDesignObject"
      :is-loading="isShortcutsLoading"
    />
    <div
      class="page-content"
      :class="{
        'w-auto rounded-3xl mx-auto px-24px py-12px bg-background-neutral-subtle':
          pageDesign === 'msn',
      }"
    >
      <!-- Category label for mobile devices -->
      <LazyTabItem
        :label="selectedCategory"
        :selected="true"
        :design="pageDesign"
        class="m-auto lg:hidden"
      />
      <LazyTab
        v-model:selected="selectedCategory"
        :tabs="settings.tabCategories"
        class="mx-auto"
        :design="pageDesign"
      />
      <div id="news-content" tabindex="-1">
        <div v-if="shouldShowTestSection" :class="leaderboardAdClass">
          <component
            :is="CurrentSection"
            :key="selectedCategory"
            @used-articles="handleUsedArticles"
            @loading="handleTestSectionLoading"
          />
        </div>
        <!-- Only show AutoLayout when test section is done loading -->
        <LazyAutoLayout
          v-show="!isTestSectionLoading"
          :key="selectedCategory"
          :loading="layoutSkeleton === undefined || !headlines.length"
          :headlines="filteredHeadlines"
          :video-headlines="videoHeadlines"
          :skeleton="layoutSkeleton"
          :fetch-more-items="handleFetchMoreItems"
          :has-more-items="hasMore || hasMoreVideos"
          :direct-ad-campaigns="directAdCampaigns || []"
          :design="pageDesign"
        />
      </div>
    </div>
    <LazyFooter
      v-if="isFooterVisible"
      id="footer-content"
      footer-position="fixed"
      :title="settings.footerBrandName ?? settings.siteName"
    />
  </div>
</template>
